/**
 * Assessment Summary View (read-only)
 * Total score, score per component/phase/theme, maturity level, date, user.
 * No question-level answers. No delete.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import './AssessmentHistory.css';

function formatDate(d) {
  if (!d) return '—';
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleDateString(undefined, { dateStyle: 'medium' }) + ' ' + date.toLocaleTimeString(undefined, { timeStyle: 'short' });
}

const AssessmentSummaryView = ({ run, instrumentType, instrumentName, onBack }) => {
  const { t, i18n } = useTranslation(['qms', 'common']);
  const useRo = (i18n.language || 'ro').startsWith('ro');

  if (!run) {
    return (
      <div className="assessment-summary">
        <p>{t('common:error')}</p>
        {onBack && <button type="button" className="btn btn-secondary" onClick={onBack}>{t('common:back') || 'Back'}</button>}
      </div>
    );
  }

  const userName = run.completedByName || run.completedBy || '—';
  const dateStr = formatDate(run.completedAt);

  if (instrumentType === 'checklist753') {
    const totalPercentage = run.totalPercentage ?? (run.totalApplicable ? (run.totalScore / run.totalApplicable) * 100 : 0);
    const themeScores = run.themeScores || [];
    return (
      <div className="assessment-summary">
        <div className="assessment-summary-header">
          <button type="button" className="btn btn-secondary btn-back" onClick={onBack}>← {t('common:back')}</button>
          <h2 className="assessment-summary-title">{instrumentName || run.instrumentName}</h2>
        </div>
        <div className="assessment-summary-meta">
          <span>{userName}</span>
          <span>{dateStr}</span>
        </div>
        <div className="assessment-summary-cards">
          <div className="summary-card overall">
            <div className="summary-label">{t('qms:whoAssessments.totalScore') || 'Total Score'}</div>
            <div className="summary-value">{Math.round(totalPercentage)}%</div>
            <div className="summary-detail">
              {run.totalScore} / {run.totalApplicable} {t('qms:whoAssessments.applicableQuestions') || 'applicable'}
            </div>
          </div>
        </div>
        {themeScores.length > 0 && (
          <div className="assessment-summary-section">
            <h3>{t('qms:whoAssessments.themeBreakdown') || 'Theme breakdown'}</h3>
            <div className="phase-stats">
              {themeScores.map((theme, idx) => (
                <div key={theme.themeId || idx} className="phase-stat-card">
                  <div className="phase-stat-name">{theme.nameRo || theme.name}</div>
                  <div className="phase-stat-bar">
                    <div className="phase-stat-fill" style={{ width: `${theme.percentage || 0}%` }} />
                  </div>
                  <div className="phase-stat-text">
                    {theme.percentage}% ({theme.score}/{theme.applicable})
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (instrumentType === 'checklist') {
    const phaseCompletions = run.phaseCompletions || [];
    const completionPercentage = run.completionPercentage ?? 0;
    return (
      <div className="assessment-summary">
        <div className="assessment-summary-header">
          <button type="button" className="btn btn-secondary btn-back" onClick={onBack}>← {t('common:back')}</button>
          <h2 className="assessment-summary-title">{instrumentName || run.instrumentName}</h2>
        </div>
        <div className="assessment-summary-meta">
          <span>{userName}</span>
          <span>{dateStr}</span>
        </div>
        <div className="assessment-summary-cards">
          <div className="summary-card overall">
            <div className="summary-label">{t('qms:whoAssessments.overallCompletion') || 'Overall completion'}</div>
            <div className="summary-value">{Math.round(completionPercentage)}%</div>
            <div className="summary-detail">
              {run.completedItems} / {run.totalItems} {t('qms:whoAssessments.itemsCompleted') || 'items'}
            </div>
          </div>
        </div>
        {phaseCompletions.length > 0 && (
          <div className="assessment-summary-section">
            <h3>{t('qms:whoAssessments.phaseBreakdown') || 'Phase breakdown'}</h3>
            <div className="phase-stats">
              {phaseCompletions.map((phase, idx) => (
                <div key={phase.phaseId || idx} className="phase-stat-card">
                  <div className="phase-stat-name">{useRo ? (phase.phaseNameRo || phase.phaseName) : (phase.phaseName || phase.phaseNameRo)}</div>
                  <div className="phase-stat-bar">
                    <div className="phase-stat-fill" style={{ width: `${phase.completionPercentage || 0}%` }} />
                  </div>
                  <div className="phase-stat-text">
                    {Math.round(phase.completionPercentage || 0)}% ({phase.completedItems}/{phase.totalItems})
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // scored (HHSAF, PSA, IPCAF)
  const totalScore = run.totalScore ?? 0;
  const maxScore = run.maxPossibleScore ?? 0;
  const scorePct = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
  const maturityLevel = run.maturityLevel;
  const maturityLabel = (maturityLevel?.labelRo || maturityLevel?.label || maturityLevel?.level) ?? '';
  const componentScores = run.componentScores || [];

  return (
    <div className="assessment-summary">
      <div className="assessment-summary-header">
        <button type="button" className="btn btn-secondary btn-back" onClick={onBack}>← {t('common:back')}</button>
        <h2 className="assessment-summary-title">{instrumentName || run.instrumentName}</h2>
      </div>
      <div className="assessment-summary-meta">
        <span>{userName}</span>
        <span>{dateStr}</span>
      </div>
      <div className="assessment-summary-cards">
        <div className="summary-card score">
          <div className="summary-label">{t('qms:whoAssessments.totalScore') || 'Total score'}</div>
          <div className="summary-value">{totalScore} / {maxScore}</div>
          {maxScore > 0 && <div className="summary-detail">{Math.round(scorePct)}%</div>}
        </div>
        {maturityLabel && (
          <div className="summary-card maturity">
            <div className="summary-label">{t('qms:whoAssessments.maturityLevel') || 'Maturity level'}</div>
            <div className="summary-value maturity-label">{maturityLabel}</div>
          </div>
        )}
      </div>
      {componentScores.length > 0 && (
        <div className="assessment-summary-section">
          <h3>{t('qms:whoAssessments.componentBreakdown') || 'Component breakdown'}</h3>
          <div className="component-stats">
            {componentScores.map((comp, idx) => {
              const compMax = comp.maxScore || 0;
              const compScore = comp.score || 0;
              const pct = compMax > 0 ? Math.round((compScore / compMax) * 100) : 0;
              const name = useRo ? (comp.componentNameRo || comp.componentName) : (comp.componentName || comp.componentNameRo);
              return (
                <div key={comp.componentId || idx} className="component-stat-card">
                  <div className="component-stat-name">{name}</div>
                  <div className="component-stat-bar">
                    <div className={`component-stat-fill ${pct < 50 ? 'low' : ''}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="component-stat-text">{compScore} / {compMax} ({pct}%)</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentSummaryView;
